import PropTypes from 'prop-types';
import React, {useState, useEffect} from 'react';
import {useDispatch} from 'react-redux';
import {Button, Grid, Popup, Dropdown, Form} from 'semantic-ui-react';

import {useHttpClient} from 'hooks';
import httpClient from 'httpClient';
import {addError, addSuccessMessage} from 'state/actions';

import styles from './BackupRestoration.module.scss';

function compileBackupOptions(backups) {
  const options = [];
  for (const [envName, envBackups] of Object.entries(backups)) {
    options.push(
      <React.Fragment key={envName}>
        <Dropdown.Divider className={styles.DropdownDivider} />
        <div className={styles.DropdownHeader}>
          <Dropdown.Header icon="drupal" content={envName} />
        </div>
        <Dropdown.Divider className={styles.DropdownDivider} />
      </React.Fragment>
    );

    for (const backup of envBackups) {
      options.push({
        key: backup,
        text: backup,
        value: backup,
      });
    }
  }
  return options;
}

const BackupRestoration = ({projectName, siteName, disabled}) => {
  const dispatch = useDispatch();
  const [restoreBackupName, setRestoreBackupName] = useState('');
  const {data, error, loading, cancel} = useHttpClient({
    url: `/api/drupal/${projectName}/drupalsites/backups`,
    method: 'GET',
  });
  const options = loading || error || !data?.backups ? [] : compileBackupOptions(data.backups);

  useEffect(() => {
    return () => {
      cancel('Request to fetch backups has been cancelled since the component has been unmounted.');
    };
  }, [cancel]);

  const restoreBackupHandler = async () => {
    if (restoreBackupName && !error && !disabled) {
      const {
        data: {message},
      } = await httpClient.post(`/api/drupal/${projectName}/drupalsites/${siteName}/frombackup`, {
        restoreBackupName,
      });

      setRestoreBackupName('');
      dispatch(addSuccessMessage(message));
    } else {
      dispatch(addError('Please, select a backup to restore'));
    }
  };

  return (
    <Form>
      <Grid columns={3} stackable>
        <Grid.Row>
          <Grid.Column textAlign="right" verticalAlign="middle" width={5}>
            <Popup
              content="Select the backup to restore"
              position="left center"
              trigger={<label htmlFor="name">Restore from backup</label>}
            />
          </Grid.Column>
          <Grid.Column verticalAlign="middle" width={7}>
            <Dropdown
              name="name"
              value={restoreBackupName}
              options={options}
              placeholder="Backup to restore"
              onChange={(_evt, data) => {
                setRestoreBackupName(data.value);
              }}
              noResultsMessage="No backups available"
              loading={loading}
              fluid
              selection
              clearable
              search
            />
          </Grid.Column>
          <Grid.Column verticalAlign="middle" width={4}>
            <Button
              type="submit"
              className={styles.SaveButton}
              onClick={() => {
                restoreBackupHandler();
              }}
              disabled={!restoreBackupName || !!error || disabled}
            >
              Restore
            </Button>
          </Grid.Column>
        </Grid.Row>
      </Grid>
    </Form>
  );
};

BackupRestoration.propTypes = {
  projectName: PropTypes.string.isRequired,
  siteName: PropTypes.string.isRequired,
  disabled: PropTypes.bool.isRequired,
};

export default BackupRestoration;
